import { useData } from "@hooks/useData";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { components } from "@apiSchema";
import { ID } from "@models";
import { iLTO, LTO } from "@models/LTO";
import { iLTOChart, LTOChart } from "@models/LTOChart";

export interface LaunchActivityMonth {
    year: number;
    month: string;
    value: number;
}

export interface LaunchRankingItem {
    id: ID;
    name: string;
    count: number;
}

export interface CategoryProfileLaunches {
    activity: {
        bySeason: iLTOChart[];
        byMonth: LaunchActivityMonth[];
    };
    topFoodsFlavors: LaunchRankingItem[];
    topChains: LaunchRankingItem[];
    recentItems: iLTO[];
    topItems: iLTO[];
}

export const EMPTY_CATEGORY_PROFILE_LAUNCHES: CategoryProfileLaunches = {
    activity: { bySeason: [], byMonth: [] },
    topFoodsFlavors: [],
    topChains: [],
    recentItems: [],
    topItems: [],
};

const REQUEST_TYPE: keyof typeof apiConfig = "get category profile launches";

function mapSeasons(
    bySeason: components["schemas"]["SnapLaunchesChartBySeason"][] | null | undefined,
): iLTOChart[] {
    return (bySeason ?? []).map(({ year, quarters }) => ({
        year: year ?? 0,
        quarters: (quarters ?? []).map(q => ({
            season: LTOChart.getSeasonEnumValue((q.season ?? "").slice(0, 3).toUpperCase()),
            value: q.value ?? 0,
        })),
    }));
}

function mapMonths(
    byMonth: components["schemas"]["SnapLaunchesChartByMonth"][] | null | undefined,
): LaunchActivityMonth[] {
    return (byMonth ?? []).flatMap(({ year, months }) =>
        (months ?? []).map(m => ({
            year: year ?? 0,
            month: m.month ?? "",
            value: m.value ?? 0,
        })),
    );
}

function mapLtos(
    wrapper: components["schemas"]["SRLtoWithScoresDataViewModelListChainPermissionWrapper"] | undefined,
): iLTO[] {
    return (wrapper?.data ?? []).map(apiModel => new LTO({ apiModel }));
}

function mapCategoryProfileLaunches(
    response: components["schemas"]["SnapCategoryProfileLaunchesViewModel"],
): CategoryProfileLaunches {
    return {
        activity: {
            bySeason: mapSeasons(response.chainItemLaunches?.bySeason),
            byMonth: mapMonths(response.chainItemLaunches?.byMonth),
        },
        topFoodsFlavors: (response.topFoodsFlavors ?? []).map(item => ({
            id: item.wordId ?? 0,
            name: item.wordName ?? "",
            count: item.launchCount ?? 0,
        })),
        topChains: (response.mostActiveChains ?? []).map(item => ({
            id: item.chainId ?? 0,
            name: item.name ?? "",
            count: item.count ?? 0,
        })),
        recentItems: mapLtos(response.recentLaunches?.mostRecent),
        topItems: mapLtos(response.recentLaunches?.topPerforming),
    };
}

async function getCategoryProfileLaunches(id: ID): Promise<CategoryProfileLaunches> {
    try {
        const response: components["schemas"]["SnapCategoryProfileLaunchesViewModel"] =
            await http.get(REQUEST_TYPE, { id });
        return mapCategoryProfileLaunches(response);
    } catch (error) {
        console.error("Fetch error:", REQUEST_TYPE, error);
        return EMPTY_CATEGORY_PROFILE_LAUNCHES;
    }
}

export default function useCategoryProfileLaunches(id: ID) {
    return useData<CategoryProfileLaunches>(EMPTY_CATEGORY_PROFILE_LAUNCHES, getCategoryProfileLaunches, id);
}
